import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from './components/error-boundary';
import { LoadingState } from './components/loading-state';
import { RequireRole } from './components/require-role';
import AppLayout from './layouts/app-layout';
import LoginPage from './pages/auth/login';
import TokenPage from './pages/auth/token';

// Wrapper pour relancer automatiquement le chargement après un redeploy
function lazyWithReload<T extends { default: React.ComponentType }>(
    importFn: () => Promise<T>,
): React.LazyExoticComponent<T['default']> {
    return lazy(() =>
        importFn().catch(() => {
            window.location.reload();
            return new Promise<T>(() => {}); // ne résout jamais, le reload prend le relais
        }),
    );
}

// Lazy load des pages principales
const DashboardPage = lazyWithReload(() => import('./pages/dashboard'));
const DashboardTeleventePage = lazyWithReload(() => import('./pages/dashboard-televente'));
const QuotesPage = lazyWithReload(() => import('./pages/quotes/listing'));
const QuoteDetailPage = lazyWithReload(() => import('./pages/quotes/quote'));
const DevisRedirectPage = lazyWithReload(() => import('./pages/quotes/devis-redirect'));
const ContactsPage = lazyWithReload(() => import('./pages/contacts/listing'));
const ContactDetailPage = lazyWithReload(() => import('./pages/contacts/contact'));
const TasksPage = lazyWithReload(() => import('./pages/tasks/listing'));
const TaskDetailPage = lazyWithReload(() => import('./pages/tasks/task'));
const CallNotificationsPage = lazyWithReload(() => import('./pages/call-notifications/listing'));
const NouveauDevisPage = lazyWithReload(() => import('./pages/nouveau-devis'));
const TaskTimingsPage = lazyWithReload(() => import('./pages/settings/task-timings'));

function App() {
    return (
        <ErrorBoundary>
            <Routes>
                {/* Public routes */}
                <Route
                    path="/auth/login"
                    element={<LoginPage />}
                />
                <Route
                    path="/auth/token"
                    element={<TokenPage />}
                />

                {/* Protected routes */}
                <Route element={<AppLayout />}>
                    <Route
                        path="/"
                        element={
                            <Navigate
                                to="/quotes"
                                replace
                            />
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <RequireRole roles={['admin', 'gestionnaire televente', 'manager televente']}>
                                <Suspense fallback={<LoadingState />}>
                                    <DashboardPage />
                                </Suspense>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/dashboard/televente"
                        element={
                            <RequireRole roles={['admin', 'gestionnaire televente', 'manager televente', 'animateur televente']}>
                                <Suspense fallback={<LoadingState />}>
                                    <DashboardTeleventePage />
                                </Suspense>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/nouveau-devis"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <NouveauDevisPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/quotes"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <QuotesPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/quotes/devis/:idDevis"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <DevisRedirectPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/quotes/:id"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <QuoteDetailPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/contacts"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <ContactsPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/contacts/:id"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <ContactDetailPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/tasks"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <TasksPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/tasks/:id"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <TaskDetailPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/call-notifications"
                        element={
                            <Suspense fallback={<LoadingState />}>
                                <CallNotificationsPage />
                            </Suspense>
                        }
                    />

                    <Route
                        path="/settings/task-timings"
                        element={
                            <RequireRole roles={['admin', 'manager televente']}>
                                <Suspense fallback={<LoadingState />}>
                                    <TaskTimingsPage />
                                </Suspense>
                            </RequireRole>
                        }
                    />
                </Route>
            </Routes>
        </ErrorBoundary>
    );
}

export default App;
